import {
  ArrowLeft,
  BellRing,
  CheckCircle2,
  ChevronRight,
  Circle,
  Clock,
  Lock,
  PenLine,
  RotateCcw,
  X,
} from 'lucide-react';
import {
  BoutonMint,
  BoutonSecondaire,
  Carte,
  Compteur,
  Pastille,
} from '../ui/Primitives';
import type { PresenceAffichee, SeanceAffichee } from '../../lib/useEmargement';
import { dureeLisible, heureReunion, texteRappel } from '../../lib/emargement-affichage';
import { useValidationDirecte } from '../modifications-sensibles/ElementsDemande';
import { LiensPv } from './LiensPv';
import { ChoixBloc } from './ChoixBloc';

/**
 * Suivi de l'émargement en cours, côté formateur.
 *
 * Il répond à une seule question, celle qu'on se pose la tablette à la main :
 * qui n'a pas encore signé. Les trois états sont donc lisibles d'un coup d'œil —
 * signé, absent, en attente — et les commandes qui touchent aux statuts sont
 * ici, jamais dans le mode signature que les apprenants ont sous les yeux.
 *
 * Une signature n'est pas une demi-journée entière : chaque ligne dit aussi le
 * temps réellement passé en salle quand il diffère du créneau, et s'ouvre sur
 * le détail de l'apprenant — signature, horaires réels, correction, historique.
 */

function iconeLigne(presence: PresenceAffichee): JSX.Element {
  if (presence.signeLe) {
    return <CheckCircle2 className="h-5 w-5 shrink-0 text-codialis-mint" aria-hidden="true" />;
  }
  if (presence.statut === 'ABSENT') {
    return <X className="h-5 w-5 shrink-0 text-red-500" aria-hidden="true" />;
  }
  if (presence.statut === 'RETARD') {
    return <Clock className="h-5 w-5 shrink-0 text-amber-500" aria-hidden="true" />;
  }
  return <Circle className="h-5 w-5 shrink-0 text-codialis-gray" aria-hidden="true" />;
}

function etatLigne(presence: PresenceAffichee): { texte: string; ton: string } {
  if (presence.signeLe) {
    return { texte: `Signé à ${heureReunion(presence.signeLe)}`, ton: 'success' };
  }
  if (presence.statut === 'ABSENT') {
    return {
      texte: presence.justifiee ? 'Absent · justifié' : 'Absent',
      ton: presence.justifiee ? 'warning' : 'danger',
    };
  }
  if (presence.statut === 'RETARD') return { texte: 'En retard · à signer', ton: 'warning' };
  return { texte: 'En attente de signature', ton: 'neutral' };
}

/** « Retard · 2h sur 4h » quand la ligne n'a pas couvert le créneau entier. */
function tempsReel(presence: PresenceAffichee): string | null {
  if (presence.qualification === 'PRESENT' || presence.qualification === 'ATTENDU') return null;
  if (presence.statut === 'ABSENT') return null;
  return `${presence.qualificationLibelle} · ${dureeLisible(presence.minutesPresence)} sur ${dureeLisible(presence.minutesPrevues)}`;
}

export function SuiviEmargement({
  seance,
  onRetour,
  onModeSignature,
  onRouvrirAppel,
  onDetail,
  onRouvrirFeuille,
  onSeanceMiseAJour,
  actions,
}: {
  seance: SeanceAffichee;
  onRetour: () => void;
  onModeSignature: () => void;
  onRouvrirAppel: () => void;
  /** Ouvre le détail d'un apprenant : signature, horaires réels, correction. */
  onDetail: (presenceId: string) => void;
  /**
   * Rouvre une feuille clôturée, sans rien retirer de ce qui y est signé — ou
   * le demande à la direction, à qui la réouverture revient (TF-437).
   */
  onRouvrirFeuille: () => void;
  /** La feuille relue après un geste qui la rend : le choix du bloc (TF-461). */
  onSeanceMiseAJour?: (seance: SeanceAffichee) => void;
  /** Commandes de fin de cycle propres à l'écran appelant (clôture, PV…). */
  actions?: JSX.Element;
}): JSX.Element {
  const attendus = seance.compteurs.inscrits;
  const signes = seance.compteurs.signatures;
  const absents = seance.compteurs.absents;
  const enAttente = Math.max(attendus - signes - absents, 0);
  const cloturee = seance.statut === 'CLOTUREE';
  const directe = useValidationDirecte();

  const dateLisible = new Date(seance.date).toLocaleDateString('fr-FR', {
    timeZone: 'Indian/Reunion',
    day: '2-digit',
    month: 'long',
  });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Compteur libelle="Attendus" valeur={String(attendus)} />
        <Compteur libelle="Signés" valeur={String(signes)} />
        <Compteur libelle="Absents" valeur={String(absents)} />
        <Compteur libelle="En attente" valeur={String(enAttente)} />
      </div>

      <Carte
        titre={`${seance.demiJourneeLibelle} — ${dateLisible}`}
        actions={
          <>
            <Pastille texte={seance.siteCode} ton="neutral" />
            <BoutonSecondaire onClick={onRetour}>
              <ArrowLeft className="mr-1.5 h-4 w-4" aria-hidden="true" />
              Sessions
            </BoutonSecondaire>
            {cloturee ? (
              <BoutonSecondaire onClick={onRouvrirFeuille}>
                <RotateCcw className="mr-1.5 h-4 w-4" aria-hidden="true" />
                {seance.reouvertureEnAttente
                  ? 'Réouverture demandée'
                  : directe
                    ? 'Rouvrir l’émargement'
                    : 'Demander la réouverture'}
              </BoutonSecondaire>
            ) : (
              <BoutonSecondaire onClick={onRouvrirAppel}>
                <RotateCcw className="mr-1.5 h-4 w-4" aria-hidden="true" />
                Reprendre l’appel
              </BoutonSecondaire>
            )}
            {!cloturee && enAttente > 0 && (
              <BoutonMint onClick={onModeSignature}>
                <PenLine className="mr-1.5 h-4 w-4" aria-hidden="true" />
                Mode signature
              </BoutonMint>
            )}
            {actions}
          </>
        }
        sansBordure
      >
        <div className="space-y-2 p-4 sm:p-6">
          <p className="text-xs text-codialis-gray">
            {seance.libelle}
            {seance.session && !seance.libelle.includes(seance.session)
              ? ` · ${seance.session}`
              : ''}{' '}
            · {seance.heureDebut} – {seance.heureFin}
            {seance.appelPar ? ` · appel fait par ${seance.appelPar}` : ''}
          </p>
          <ChoixBloc seance={seance} onMiseAJour={onSeanceMiseAJour} />
          {seance.rappel && (
            <p className="flex items-start gap-1.5 text-xs text-codialis-gray">
              <BellRing className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="min-w-0">{texteRappel(seance.rappel)}</span>
            </p>
          )}
          <p
            className={`flex items-start gap-1.5 rounded-xl px-3 py-2 text-xs ${
              seance.formateurSigneLe ? 'bg-codialis-mint/10 text-codialis-navy' : 'bg-amber-50 text-amber-900'
            }`}
          >
            <PenLine className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span className="min-w-0">
              {seance.formateurSigneLe
                ? `Feuille signée pour le formateur${
                    seance.formateurSignePar ? ` par ${seance.formateurSignePar}` : ''
                  } à ${heureReunion(seance.formateurSigneLe)}.`
                : 'Signature du formateur attendue : elle est reproduite sur le PV de la demi-journée.'}
            </span>
          </p>
          {cloturee && (
            <div className="space-y-2 rounded-xl bg-codialis-sand px-3 py-3">
              <p className="flex items-start gap-1.5 text-xs text-codialis-navy">
                <Lock className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span className="min-w-0">
                  {directe
                    ? 'Feuille clôturée, PV archivé. Corrigez une présence depuis le détail de l’apprenant, ou rouvrez la feuille : chaque correction est inscrite au registre des modifications sensibles.'
                    : seance.correctionDirecte
                      ? 'Feuille clôturée, PV archivé. Corrigez une présence depuis le détail de l’apprenant, motif à l’appui : la correction est inscrite au registre des modifications sensibles. La réouverture de la feuille se demande à la direction.'
                      : 'Feuille clôturée, PV archivé. Une correction passe par la direction : demandez-la depuis le détail de l’apprenant. Les signatures sont conservées.'}
                </span>
              </p>
              <LiensPv seance={seance} />
            </div>
          )}

          <ul className="divide-y divide-gray-100">
            {seance.presences.map((presence) => {
              const etat = etatLigne(presence);
              const reel = tempsReel(presence);
              return (
                <li key={presence.id}>
                  <button
                    type="button"
                    onClick={() => onDetail(presence.id)}
                    aria-label={`Détail de ${presence.apprenant}`}
                    className="flex min-h-[44px] w-full flex-wrap items-center justify-between gap-2 rounded-xl px-1 py-3 text-left transition hover:bg-gray-50 [@media(pointer:coarse)]:min-h-[48px]"
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      {iconeLigne(presence)}
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-codialis-navy">
                          {presence.apprenant}
                        </p>
                        {reel && <p className="truncate text-xs text-amber-700">{reel}</p>}
                        {presence.motifJustification && (
                          <p className="truncate text-xs text-codialis-gray">
                            {presence.motifJustification}
                          </p>
                        )}
                      </div>
                    </div>
                    <span className="flex shrink-0 items-center gap-1.5">
                      {presence.demandeEnAttente && <Pastille texte="Correction demandée" ton="warning" />}
                      <Pastille texte={etat.texte} ton={etat.ton} />
                      <ChevronRight className="h-4 w-4 text-codialis-gray" aria-hidden="true" />
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </Carte>
    </div>
  );
}
